import styled from "styled-components";

import { colorsPalettes } from "@similarweb/styles";
import { Text } from "@similarweb/ui-components/foundations";

interface IRootStyleProps {
    $cellMinWidth: number;
    $cellHeight: number;
    $gap: number;
}

interface IGridContainerProps {
    $gridTemplateColumns: string;
    $gridWidth: string;
    $hasHeaderRow: boolean;
}

interface IAxisContainerProps {
    $hasHeaderRow: boolean;
}

interface IGridPositionProps {
    $gridColumn: number;
    $gridRow: number;
}

interface IAxisLabelProps {
    $align?: "center" | "flex-start" | "flex-end";
}

interface IXAxisLabelProps extends IAxisLabelProps, IGridPositionProps {}

interface ICellProps extends IGridPositionProps {
    $color: string;
}

interface IScrollFadeProps {
    $side: "start" | "end";
    $visible: boolean;
}

const SLOT = "data-slot";

export const Root = styled.div<IRootStyleProps>`
    /* Grid layout */
    --heatmap-cell-min-width: ${({ $cellMinWidth }) => $cellMinWidth}px;
    --heatmap-cell-height: ${({ $cellHeight }) => $cellHeight}px;
    --heatmap-gap: ${({ $gap }) => $gap}px;
    --heatmap-header-row-size: minmax(32px, auto);

    /* Cell */
    --heatmap-cell-radius: 2px;
    --heatmap-cell-cursor: default;
    --heatmap-cell-hover-opacity: 0.8;

    /* Axis */
    --heatmap-axis-inline-padding: 4px;
    --heatmap-x-axis-marker-height: 8px;
    --heatmap-x-axis-marker-width: 1px;
    --heatmap-x-axis-marker-gap: 4px;
    --heatmap-x-axis-marker-offset-bottom: 4px;
    --heatmap-x-axis-marker-color: ${colorsPalettes.midnight[50]};
    --heatmap-x-axis-marker-opacity: 1;
    --heatmap-y-axis-inline-padding: 8px;

    /* Legend */
    --heatmap-legend-gap: 4px;
    --heatmap-legend-margin-top: 12px;

    /* Scroll & surface */
    --heatmap-surface-color: Canvas;
    --heatmap-scroll-fade-size: 12px;
    --heatmap-scroll-fade-color: var(--heatmap-surface-color);
    --heatmap-scrollbar-clearance: 12px;

    display: flex;
    flex-direction: column;
    width: 100%;
    min-width: 0;
    background: var(--heatmap-surface-color);
`;

/* ─── Scrollable grid wrapper ─────────────────────────────── */

export const GridLayout = styled.div.attrs({
    className: "heat-map__grid-layout",
    [SLOT]: "grid-layout",
})`
    display: flex;
    align-items: flex-start;
    gap: var(--heatmap-gap);
    width: 100%;
    min-width: 0;
    overflow: hidden;
`;

export const GridViewport = styled.div.attrs({
    className: "heat-map__grid-viewport",
    [SLOT]: "grid-viewport",
})`
    position: relative;
    flex: 1 1 0;
    min-width: 0;
    max-width: 100%;
    overflow: hidden;
`;

export const GridScroller = styled.div.attrs({
    className: "heat-map__grid-scroller",
    [SLOT]: "grid-scroller",
})`
    width: 100%;
    min-width: 0;
    padding-bottom: var(--heatmap-scrollbar-clearance);
    overflow-x: auto;
    overflow-y: hidden;
`;

export const ScrollFade = styled.div.attrs<IScrollFadeProps>(({ $side }) => ({
    className: `heat-map__scroll-fade heat-map__scroll-fade--${$side}`,
    [SLOT]: `${$side}-scroll-fade`,
}))<IScrollFadeProps>`
    position: absolute;
    top: 0;
    bottom: 0;
    ${({ $side }) => ($side === "start" ? "left: 0;" : "right: 0;")}
    z-index: 1;
    width: var(--heatmap-scroll-fade-size);
    background: linear-gradient(
        ${({ $side }) => ($side === "start" ? "to right" : "to left")},
        var(--heatmap-scroll-fade-color),
        transparent
    );
    opacity: ${({ $visible }) => ($visible ? 1 : 0)};
    pointer-events: none;
    transition: opacity 0.2s ease;
`;

/* ─── Grid (CSS Grid with cells) ─────────────────────────── */

export const GridContainer = styled.div.attrs({
    className: "heat-map__grid",
    [SLOT]: "grid",
})<IGridContainerProps>`
    display: grid;
    grid-template-columns: ${({ $gridTemplateColumns }) => $gridTemplateColumns};
    grid-template-rows: ${({ $hasHeaderRow }) => ($hasHeaderRow ? "var(--heatmap-header-row-size)" : "none")};
    grid-auto-rows: var(--heatmap-cell-height);
    align-items: stretch;
    gap: var(--heatmap-gap);
    width: ${({ $gridWidth }) => $gridWidth};
    min-width: ${({ $gridWidth }) => $gridWidth};
`;

export const Cell = styled.div.attrs({
    className: "heat-map__cell",
    [SLOT]: "cell",
})<ICellProps & { $clickable?: boolean }>`
    grid-column: ${({ $gridColumn }) => $gridColumn};
    grid-row: ${({ $gridRow }) => $gridRow};
    width: 100%;
    height: 100%;
    min-width: 0;
    border-radius: var(--heatmap-cell-radius);
    background: ${({ $color }) => $color};
    cursor: ${({ $clickable }) => ($clickable ? "pointer" : "var(--heatmap-cell-cursor)")};
    content-visibility: auto;
    contain-intrinsic-size: auto var(--heatmap-cell-min-width) var(--heatmap-cell-height);
    transition: opacity 0.15s ease;

    &:hover {
        opacity: var(--heatmap-cell-hover-opacity);
    }
`;

/* ─── X-axis ─────────────────────────────────────────────── */

export const AxisLabel = styled(Text).attrs({
    className: "heat-map__x-axis-label",
    "data-slot": "x-axis-label",
})<IXAxisLabelProps>`
    grid-column: ${({ $gridColumn }) => $gridColumn};
    grid-row: ${({ $gridRow }) => $gridRow};
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: flex-end;
    gap: var(--heatmap-x-axis-marker-gap);
    width: 100%;
    min-width: 0;
    padding: 0 var(--heatmap-axis-inline-padding) var(--heatmap-x-axis-marker-offset-bottom) 2px;
    white-space: nowrap;
    text-align: left;
`;

export const AxisMarker = styled.div.attrs({
    className: "heat-map__x-axis-marker",
    [SLOT]: "x-axis-marker",
})`
    flex: 0 0 auto;
    align-self: flex-start;
    width: var(--heatmap-x-axis-marker-width);
    height: var(--heatmap-x-axis-marker-height);
    background: var(--heatmap-x-axis-marker-color);
    opacity: var(--heatmap-x-axis-marker-opacity);
`;

/* ─── Y-axis ─────────────────────────────────────────────── */

export const AxisContainer = styled.div.attrs({
    className: "heat-map__y-axis",
    [SLOT]: "y-axis",
})<IAxisContainerProps>`
    display: grid;
    flex: 0 0 auto;
    ${({ $hasHeaderRow }) => ($hasHeaderRow ? "grid-template-rows: var(--heatmap-header-row-size);" : "")}
    grid-auto-rows: var(--heatmap-cell-height);
    gap: var(--heatmap-gap);
    width: max-content;
`;

export const YAxisSpacer = styled.div.attrs({
    className: "heat-map__y-axis-spacer",
    [SLOT]: "y-axis-spacer",
})`
    width: 100%;
`;

export const YAxisLabel = styled(Text).attrs({
    className: "heat-map__y-axis-label",
    "data-slot": "y-axis-label",
})<IAxisLabelProps>`
    display: flex;
    align-items: center;
    justify-content: ${({ $align = "center" }) => $align};
    min-width: max-content;
    height: 100%;
    padding: 0 var(--heatmap-y-axis-inline-padding);
    white-space: nowrap;
`;

export const YAxisHeader = styled(YAxisLabel).attrs({
    className: "heat-map__y-axis-header",
    "data-slot": "y-axis-header",
})`
    font-weight: 700;
`;

/* ─── Legend ──────────────────────────────────────────────── */

export const LegendContainer = styled(Text).attrs({
    className: "heat-map__legend",
    "data-slot": "legend",
})`
    display: flex;
    align-items: center;
    gap: var(--heatmap-legend-gap);
    margin-top: var(--heatmap-legend-margin-top);
`;

export const LegendCell = styled.div.attrs({
    className: "heat-map__legend-cell",
    [SLOT]: "legend-cell",
})<{ $color: string }>`
    width: 14px;
    height: 14px;
    border-radius: var(--heatmap-cell-radius);
    background: ${({ $color }) => $color};
`;
